import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import type {
  ProfileSessionNavigate,
  ProfileSessionNavigationTarget,
  ProfileSessionRoute,
  ProfileSessionRouteKind
} from "@/features/navigation";

import { APP_PATHS } from "../routes";

export interface ProfileSessionRouterLocation {
  readonly hash: string;
  readonly pathname: string;
  readonly search: string;
}

export interface ProfileSessionRouterAdapter {
  readonly navigate: ProfileSessionNavigate;
  readonly route: ProfileSessionRoute;
}

function matchesLeafPath(pathname: string, path: string): boolean {
  return pathname === path || pathname === path + "/";
}

function matchesPlayPath(pathname: string): boolean {
  if (matchesLeafPath(pathname, APP_PATHS.play)) {
    return true;
  }

  const prefix = APP_PATHS.play + "/";
  if (!pathname.startsWith(prefix)) {
    return false;
  }

  const remainder = pathname.slice(prefix.length);
  const levelSegment = remainder.endsWith("/")
    ? remainder.slice(0, -1)
    : remainder;

  return levelSegment.length > 0 && !levelSegment.includes("/");
}

function getRouteKind(pathname: string): ProfileSessionRouteKind {
  if (matchesLeafPath(pathname, APP_PATHS.onboarding)) {
    return "onboarding";
  }

  if (matchesLeafPath(pathname, APP_PATHS.map)) {
    return "map";
  }

  if (matchesPlayPath(pathname)) {
    return "play";
  }

  if (
    pathname === APP_PATHS.parent ||
    pathname.startsWith(APP_PATHS.parent + "/")
  ) {
    return "parent";
  }

  return "other";
}

export function toProfileSessionRoute(
  location: ProfileSessionRouterLocation
): ProfileSessionRoute {
  return {
    kind: getRouteKind(location.pathname),
    pathname: location.pathname + location.search + location.hash
  };
}

export function toProfileSessionPath(
  target: ProfileSessionNavigationTarget
): string {
  switch (target.kind) {
    case "map":
      return APP_PATHS.map;
    case "onboarding":
      return APP_PATHS.onboarding;
    case "parent":
      return APP_PATHS.parent;
    case "level":
      return APP_PATHS.play + "/" + encodeURIComponent(target.levelId);
    case "path":
      return target.pathname;
  }
}

export function useProfileSessionRouter(): ProfileSessionRouterAdapter {
  const location = useLocation();
  const routerNavigate = useNavigate();
  const route = useMemo(
    () =>
      toProfileSessionRoute({
        hash: location.hash,
        pathname: location.pathname,
        search: location.search
      }),
    [location.hash, location.pathname, location.search]
  );
  const navigate = useCallback<ProfileSessionNavigate>(
    (intent) => {
      routerNavigate(toProfileSessionPath(intent.target), {
        replace: intent.history === "replace"
      });
    },
    [routerNavigate]
  );

  return useMemo(
    () => ({ navigate, route }),
    [navigate, route]
  );
}
